import Button from 'assets/components/button/Button';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import Icon from 'assets/components/icon/Icon';
import SelectInput from 'assets/components/inputs/select/SelectInput';
import { getLocales } from 'assets/locales/Locale';
import Theme from 'assets/themes/Theme.Common';
import {
  AutomationRuleLike,
  AutomationRuleSchema,
  isValueExpressionType,
  isValuePropertyExpressionType,
} from '../helpers/AutomationTypes';
import {
  getExpressionTypeDefaultValue,
  getPropertyMetadata,
  getSupportedExpressionTypes,
  isExpressionPropertyPathMissing,
  updateExpression,
} from '../helpers/ruleHelpers';
import AutomationExpressionValueInput from './Automation.Expression.Value.Input';

type Props = {
  rule: AutomationRuleLike;
  expression: Model.Automation.IExpression;
  schema: AutomationRuleSchema;
  readonly?: boolean;
  hidePrefix?: boolean;
  onChange(rule: AutomationRuleLike): void;
  onDelete?(): void;
};

export default function AutomationExpressionEditor(props: Props) {
  const { lang, translate } = getLocales();
  const metadata = getPropertyMetadata(props.schema, props.expression.propertyPath);
  const hasExpressionType = Boolean(props.expression.type);
  const isValueExpression = isValueExpressionType(props.expression.type);
  const propertyPathError = isExpressionPropertyPathMissing(props.expression) ? lang.required : undefined;
  const propertyOptions = props.schema.expressionProperties.map((propertyPath) => ({
    id: propertyPath,
    title: propertyPath,
    htmlElementProps: { title: propertyPath },
  }));
  const supportedExpressionTypes = getSupportedExpressionTypes(
    props.schema,
    isValueExpression ? undefined : props.expression.propertyPath
  );

  const onChangeInternal = (updatedExpression: Partial<Model.Automation.IExpression>) => {
    props.onChange(updateExpression(props.rule, props.expression.uid, updatedExpression));
  };

  const getFirstPropertyPathForType = (type: Model.Enum.Automation.ExpressionTypes | undefined) =>
    props.schema.expressionProperties.find((propertyPath) =>
      getPropertyMetadata(props.schema, propertyPath)?.supportedExpressions.includes(type)
    ) || '';

  const createTypePatch = (type: Model.Enum.Automation.ExpressionTypes | undefined) => {
    if (!type) {
      return {
        propertyPath: '',
        type,
        value: '',
      };
    }

    if (isValueExpressionType(type)) {
      return {
        propertyPath: null,
        type,
        value: getExpressionTypeDefaultValue(props.schema, type),
      };
    }

    const nextPropertyPath = metadata?.supportedExpressions.includes(type)
      ? props.expression.propertyPath
      : getFirstPropertyPathForType(type);
    const nextMetadata = getPropertyMetadata(props.schema, nextPropertyPath);

    return {
      propertyPath: nextPropertyPath,
      type,
      value: getExpressionTypeDefaultValue(props.schema, type, nextMetadata),
    };
  };

  const renderPropertyPathInput = () => (
    <SelectInput
      name="propertyPath"
      data={propertyOptions}
      value={props.expression.propertyPath}
      onChange={({ propertyPath }) => {
        const nextMetadata = getPropertyMetadata(props.schema, propertyPath);
        const nextType = nextMetadata?.supportedExpressions.includes(props.expression.type)
          ? props.expression.type
          : nextMetadata?.supportedExpressions?.[0];

        onChangeInternal({
          propertyPath,
          type: nextType,
          value: isValuePropertyExpressionType(nextType)
            ? null
            : getExpressionTypeDefaultValue(props.schema, nextType, nextMetadata),
        });
      }}
      readOnly={props.readonly || props.schema.isPropertySelectorDisabled || !hasExpressionType}
      disabled={props.readonly || props.schema.isPropertySelectorDisabled || !hasExpressionType}
      error={propertyPathError}
    />
  );

  const renderExpressionTypeInput = () => (
    <SelectInput
      name="type"
      data={supportedExpressionTypes.map((it) => ({
        id: it,
        title: translate(it),
      }))}
      value={props.expression.type}
      readOnly={props.readonly}
      disabled={props.readonly}
      onChange={({ type }) => onChangeInternal(createTypePatch(type))}
    />
  );

  const renderValueInput = () => (
    <AutomationExpressionValueInput
      expression={props.expression}
      schema={props.schema}
      readonly={props.readonly}
      onChangeValue={(value) => onChangeInternal({ value })}
    />
  );

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: Theme.Size.S }}>
      {isValueExpression ? (
        <>
          {renderExpressionTypeInput()}
          {renderValueInput()}
        </>
      ) : (
        <>
          {renderPropertyPathInput()}
          {renderExpressionTypeInput()}
          {renderValueInput()}
        </>
      )}
      {!props.readonly && props.onDelete && (
        <Button class={ThemeButtonCircle} media={<Icon class="fas-trash" />} onClick={props.onDelete} />
      )}
    </div>
  );
}
